import { useState } from 'react'
import { Button } from './Button.tsx'

/**
 * Whether a case counts.
 *
 * `Situação` is not decoration and it is not progress: it is the difference
 * between a case that is measured against and one that is ignored. A draft is
 * a case nobody has confirmed, and every eval skips it -- a target nobody
 * checked bakes its own errors into the measurement and makes improvement
 * unmeasurable.
 *
 * Which is why this needs a button. Both golden sets had the flag and no way
 * to change it, so every case sat at `draft` forever and the evals quietly
 * measured nothing. A rule that cannot be satisfied is a rule that silently
 * turns the feature off.
 *
 * Shared by the reading and resolution screens, because the meaning is
 * identical and two implementations would drift into two meanings.
 */
export function CaseStatus({
  status,
  onChange,
  busy = false,
  undecided,
}: {
  status: string
  onChange: (next: 'draft' | 'corrected') => void
  busy?: boolean
  /**
   * Why this case cannot be promoted yet, if it cannot.
   *
   * Shown instead of the button rather than beside a disabled one: "you may
   * not do this" is only useful with the reason attached.
   */
  undecided?: string | undefined
}) {
  const [confirming, setConfirming] = useState(false)
  const live = status === 'corrected'

  return (
    <div className="rounded-base border-stroke-weak bg-background-raised flex flex-wrap items-center gap-3 border p-3">
      <span className="text-tTiny text-text-weak font-semibold uppercase">Situação</span>

      <span
        className={`rounded-base text-tTiny px-2 py-0.5 font-semibold ${
          live ? 'bg-fill-brand-weak text-text-brand' : 'bg-fill-weak text-text-weak'
        }`}
      >
        {live ? 'Conferido' : 'Rascunho'}
      </span>

      <span className="text-tSmall text-text-weak max-w-prose">
        {live
          ? 'Este caso entra nas medições. Uma mudança aqui muda o que "correto" significa.'
          : 'Ninguém conferiu este caso ainda, então nada é medido contra ele.'}
      </span>

      <span className="flex-1" />

      {live ? (
        /*
         * Demoting is the reversible half and needs no ceremony -- it only
         * ever removes a case from the measurement, which is the safe
         * direction.
         */
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          disabled={busy}
          onClick={() => onChange('draft')}
        >
          Voltar para rascunho
        </Button>
      ) : undecided !== undefined ? (
        <span className="text-tSmall text-text-warning max-w-prose">{undecided}</span>
      ) : confirming ? (
        <>
          <span className="text-tSmall text-text-strong">
            A partir daqui este caso passa a valer como gabarito.
          </span>
          <Button
            variant="primary"
            tone="brand"
            size="small"
            disabled={busy}
            onClick={() => {
              setConfirming(false)
              onChange('corrected')
            }}
          >
            {busy ? 'Salvando...' : 'Confirmar'}
          </Button>
          <Button
            variant="tertiary"
            tone="neutral"
            size="small"
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            Cancelar
          </Button>
        </>
      ) : (
        /*
         * Promoting asks twice. It is the click that puts a case into every
         * future measurement, and a wrong answer key is worse than no answer
         * key: it makes the numbers confidently wrong rather than absent.
         */
        <Button
          variant="secondary"
          tone="brand"
          size="small"
          disabled={busy}
          onClick={() => setConfirming(true)}
        >
          Marcar como conferido
        </Button>
      )}
    </div>
  )
}
